import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { getAdminConsentLink, type AdminConsentLinkDto } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { ADMIN_CONSENT_FAILED_TEXT, ADMIN_CONSENT_HELP_TEXT, adminConsentMessage } from '@/lib/mailboxes'

/** The two answers that mean « no Microsoft administrator to ask » — not a transient failure. */
const isNotMicrosoft = (e: unknown) => e instanceof ApiError && (e.code === 'MAILBOX_PUBLIC_DOMAIN' || e.code === 'MAILBOX_NOT_MICROSOFT')

type LinkState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; link: AdminConsentLinkDto } | { kind: 'refused'; message: string; notMicrosoft: boolean }

/**
 * SOLY-SUPPLIER-002 MS-05 — the help for a person whose company must first authorize Soly at
 * Microsoft: a ready-to-send message with the administrator's link, computed from the address alone
 * (no attempt, no state), so it can be copied again any time. After a failed return from Microsoft
 * (<paramref name="failed"/>) it also offers « Réessayer » and Microsoft's code — never « refused »
 * or « request sent »: Soly cannot know which it was.
 */
export function AdminConsentHelp({
  address,
  failed = false,
  code = null,
  onRetry,
  retryDisabled = false,
}: {
  address: string
  failed?: boolean
  code?: string | null
  onRetry?: () => void
  retryDisabled?: boolean
}) {
  const trimmed = address.trim()
  // Answers are kept with the address they are for: a stale one reads as « loading » for the new address.
  const [answer, setAnswer] = useState<{ address: string; state: LinkState } | null>(null)
  const [copyFailedFor, setCopyFailedFor] = useState<string | null>(null)

  useEffect(() => {
    if (!trimmed) return
    let current = true
    // The address may still be typed: ask once it settles.
    const timer = setTimeout(() => {
      getAdminConsentLink(trimmed)
        .then((link) => current && setAnswer({ address: trimmed, state: { kind: 'ready', link } }))
        .catch(
          (e) =>
            current &&
            setAnswer({ address: trimmed, state: { kind: 'refused', message: e instanceof ApiError ? e.message : 'Le message n’a pas pu être préparé.', notMicrosoft: isNotMicrosoft(e) } }),
        )
    }, 400)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [trimmed])

  const state: LinkState = !trimmed ? { kind: 'idle' } : answer?.address === trimmed ? answer.state : { kind: 'loading' }
  const message = state.kind === 'ready' ? adminConsentMessage(state.link.domain, state.link.url) : null
  const copyFailed = copyFailedFor !== null && copyFailedFor === message

  const copy = async () => {
    if (!message) return
    try {
      await navigator.clipboard.writeText(message)
      toast.success('Message copié.')
    } catch {
      // No clipboard (browser policy, old mobile): the message is shown to select by hand.
      setCopyFailedFor(message)
    }
  }

  return (
    <div className="space-y-3 rounded-md border bg-muted/40 p-3 text-sm" data-testid="mailboxes-admin-help">
      {/* Outside Microsoft 365 there is no administrator to ask: the refusal stands alone. Any other
          refusal (Microsoft down, an address still being typed) keeps the sentence. */}
      {!(state.kind === 'refused' && state.notMicrosoft) && <p>{failed ? ADMIN_CONSENT_FAILED_TEXT : ADMIN_CONSENT_HELP_TEXT}</p>}

      {state.kind === 'idle' && <p className="text-muted-foreground">Saisissez d’abord l’adresse de la boîte.</p>}
      {state.kind === 'loading' && <p className="text-muted-foreground">Préparation du message…</p>}
      {state.kind === 'refused' && (
        <p className="text-destructive" role="alert" data-testid="mailboxes-admin-help-refused">
          {state.message}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {message && (
          // The label is long: it wraps instead of widening the dialog past a phone's screen.
          <Button
            type="button"
            size="sm"
            className="h-auto max-w-full shrink whitespace-normal py-1.5 text-left"
            onClick={() => void copy()}
            data-testid="mailboxes-admin-help-copy-btn"
          >
            Copier le message pour votre administrateur
          </Button>
        )}
        {state.kind === 'ready' && (
          <Button type="button" size="sm" variant="outline" asChild>
            <a href={state.link.url} target="_blank" rel="noopener noreferrer" data-testid="mailboxes-admin-help-open-link">
              Ouvrir le lien
            </a>
          </Button>
        )}
        {failed && onRetry && (
          <Button type="button" size="sm" variant="outline" onClick={onRetry} disabled={retryDisabled} data-testid="mailboxes-admin-help-retry-btn">
            Réessayer
          </Button>
        )}
      </div>

      {copyFailed && message && (
        <textarea
          readOnly
          value={message}
          rows={6}
          className="w-full rounded border bg-background p-2 text-xs"
          onFocus={(e) => e.currentTarget.select()}
          data-testid="mailboxes-admin-help-message"
        />
      )}

      {code && (
        <p className="select-all text-xs text-muted-foreground" data-testid="mailboxes-admin-help-code">
          Code Microsoft : {code}
        </p>
      )}
    </div>
  )
}
